import { useState, useEffect,useCallback ,useRef } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Components
import { Box, Typography, Tooltip, IconButton, CircularProgress, } from "@mui/material";

// ** Custom Component Imports
import CustomChip from "../../../components/mui/chip";

// ** Icon Imports
import Icon from "../../../components/icon";
import { GetService } from "./GetService";
import { filterServices } from "./filterServices";
import { useServiceLogic } from "./serviceLogic";
import { DeleteService } from "./DeleteService";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { GridActionsCellItem, GridFooter, GridFooterContainer } from "@mui/x-data-grid";
import { GetBundle } from "./GetBundle";
import { filterBundle } from "./filterBundle";
import { useBundleLogic } from "./bundleLogic";
import { DeleteBundle } from "./DeleteBundle";
import { formatUSCurrency } from "components/CurrencyFormat";
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog";
import { useServicePermission } from "../permissions/ServicePermissions";
import { useTheme } from "@mui/material/styles";

export const useIndexLogic = ({toast}) => {
  //#region Permissions
  const { ServicePermissionProps } = useServicePermission();
  const {
    // Service Permissions
    hasServiceSavePermission,
    hasServiceGetPermission,
    hasServiceDeletePermission,
    hasServiceSoftDeletePermission,
    hasServiceIndustriesGetPermission,

    // Bundle Permissions
    hasBundleSavePermission,
    hasBundleGetPermission,
    hasBundleDeletePermission,
    hasBundleSoftDeletePermission
  } = ServicePermissionProps;
  //#endregion Permissions

  //#region Hooks
  const theme = useTheme();
  const navigate = useNavigate();
  //#endregion

  //#region Constants
  const contentHeightFixed = false;

  const columnsService = [
    {
      flex: 0.1,
      minWidth: 140,
      field: "serviceOfferingName",
      headerName: "Service Name",
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  color: 'text.primary',
                  textDecoration: 'underline',
                }
              }}
              onClick={() => handleEditService(row.id)}
            >
              {row.serviceOfferingName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "description",
      headerName: "Description",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.description}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "industryName",
      headerName: "Industry Name",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.industryName}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "flatRate",
      headerName: "Flat Rate",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {formatUSCurrency(row.flatRate)}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "isActive",
      headerName: "Status",
      minWidth: 120,
      renderCell: ({ row }) => {
        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={row.isActive? "success" : "secondary"} //'success' // Use color names: primary, secondary, error, warning, info, success
            label={row.isActive? "Active" : "Inactive"}
          />
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "createdBy",
      headerName: "Created By",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.createdBy}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "createDate",
      headerName: "Created On",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.createDate}
              </Typography>
            </Box>
          </Box>
        );
      },
    },   
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      type: 'actions',
      getActions: ({ id }) => [
          <GridActionsCellItem
            sx={{ transform: 'scale(1.5)', marginRight:3 }}  
            icon={<Icon icon="tabler:pencil" />}
            label="Edit"
            disabled={!hasServiceSavePermission}
            {...(hasServiceSavePermission) && {
              onClick: () => handleEditService(id)
            }}
            color="inherit"
          />,
          <GridActionsCellItem
            color="error"
            sx={{ transform: 'scale(1.5)' }}  
            icon={<Icon icon="tabler:trash" />}
            label="Delete"
            disabled={!hasServiceDeletePermission}
            {...(hasServiceDeletePermission && {
              onClick: () => showConfirmationServiceDelete(id)
            })}  
          />
      ],    
    },
  ];

  const columnsBundle = [    
    {
      flex: 0.1,
      minWidth: 140,
      maxWidth: 140,
      field: "id",
      headerName: "Bundle ID",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'text.primary',
                    textDecoration: 'underline',
                  }
                }}
                onClick={() => handleEditBundle(row.id)}
              >
                {row.id}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 180,
      field: "bundleName",
      headerName: "Bundle Name",
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'text.primary',
                    textDecoration: 'underline',
                  }
                }}
                onClick={() => handleEditBundle(row.id)}
              >
                {row.bundleName}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.2,
      minWidth: 260,
      field: "description",
      headerName: "Description",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.description}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    // {
    //   flex: 0.1,
    //   minWidth: 140,
    //   field: "industry",
    //   headerName: "Industry",
    //   renderCell: (params) => {
    //     const { row } = params;
  
    //     return (
    //       <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
    //         <Box sx={{ display: "flex", flexDirection: "column" }}>
    //           <Typography noWrap variant="body1">
    //             {row.category}
    //           </Typography>
    //         </Box>
    //       </Box>
    //     );
    //   },
    // },
    {
      flex: 0.1,
      minWidth: 140,
      maxWidth: 140,
      field: "totalFlatRate",
      headerName: "Flat Rate",
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
              {formatUSCurrency(row.totalFlatRate)}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "isActive",
      headerName: "Status",
      minWidth: 120,
      maxWidth: 120,
      renderCell: ({ row }) => {
        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={row.isActive? "success" : "secondary"} //'success' // Use color names: primary, secondary, error, warning, info, success
            label={row.isActive? "Active" : "Inactive"}
          />
        );
      },
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id }) => [        
          <GridActionsCellItem
            sx={{ transform: 'scale(1.5)', marginRight:3 }}  
            icon={<Icon icon="tabler:pencil" />}
            label="Edit"
            color="inherit"
            disabled={!hasBundleSavePermission}
            {...(hasBundleSavePermission && {
              onClick: () => handleEditBundle(id)
            })}
          />,
          <GridActionsCellItem
            color="error"
            sx={{ transform: 'scale(1.5)' }}  
            icon={<Icon icon="tabler:trash" />}
            label="Delete"
            disabled={!hasBundleDeletePermission}
            {...(hasBundleDeletePermission && {
              onClick: () => showConfirmationBundleDelete(id)
            })}
          />
      ],
    },
  ];
  //#endregion End of Constants

  //#region States

  const [loading, setLoading] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [tabValue, setTabValue] = useState("1");
  const [bundleIsEmpty, setBundleIsEmpty] = useState(true);
  const [serviceIsEmpty, setServiceIsEmpty] = useState(true);
  const [openBundle, setOpenBundle] = useState(false);
  const [openService, setOpenService] = useState(false);
  const [openBundleDlg, setOpenBundleDlg] = useState(false);
  const [openServiceDlg, setOpenServiceDlg] = useState(false);
  const [servicePaginationModel, setServicePaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [bundlePaginationModel, setBundlePaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [serviceToDelete, setServiceToDelete] = useState(null);
  //#endregion end of states

  //#region Handlers and methods

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setLoading(false);
  //   }, 2000);
  //   return () => clearTimeout(timer);
  // }, []);
 
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1); 
  const [hasMore, setHasMore] = useState(true);
  const [serviceList,setServiceList] = useState([]);
  const serviceListRef = useRef([]);
  const [serviceContent, setServiceContent] = useState({title:'Create New Service', btnText:'Save Service', notification: 'Service saved successfully.'});
  const [searchText, setSearchText] = useState('');
  const [bundleList,setBundleList] = useState([]);
  const [bundleContent, setBundleContent] = useState({title:'Create New Bundle', btnText:'Save Bundle', notification: 'Bundle saved successfully.'});
  const fetchLock = useRef(false); 

  // const fetchServices = async () => {
  //   // if (!hasMore || loading || fetchLock.current || !hasServiceGetPermission) return; // Prevent unnecessary calls
  //   if (!hasServiceGetPermission) return; // Prevent unnecessary calls
  //   // fetchLock.current = true;
  //   // setLoadingRecords(true);
  //   try {
  //     const data = await GetService(0, pageSize, pageNumber, searchText, navigate);
  //     console.log()
      
  //     if (data?.result?.length > 0) {
  //       const fileredData = filterServices(data.result);
  //       setServiceList(fileredData)
  //       serviceListRef.current = [...serviceListRef.current, ...fileredData]; // Append new data
  //       setServiceList([...serviceListRef.current]);
  //       setPageNumber(pageNumber + 1); // Increment page
  //     } else {
  //       setHasMore(false); // No more data to load
  //     }
  //   } catch (error) {
  //     console.error("Error fetching services data:", error);
  //   } finally {
  //     setLoadingRecords(false);
  //     fetchLock.current = false; 
  //   }
  // };

  const fetchServices = async () => {
  if (!hasServiceGetPermission) return;
  
  setLoading(true);
  try {
    const data = await GetService(0, 50, 1, '', navigate);
    
    if (data?.result?.length > 0) {
      const fileredData = filterServices(data.result);
      
      // Sort by serviceOfferingName A→Z by default
      const sortedData = fileredData.sort((a, b) => 
        a.serviceOfferingName?.localeCompare(b.serviceOfferingName)
      );
      
      setServiceList(sortedData);
    }
  } catch (error) {
    console.error("Error fetching services data:", error);
  } finally {
    setLoading(false)
  }
};

  useEffect(() => {
    if(hasServiceGetPermission){
      fetchServices()
    }
  }, [servicePaginationModel, hasServiceGetPermission])

  useEffect(() => {
    if(searchText.length > 2){
      setPageNumber(1);
      setHasMore(true);
      setServiceList([]);
      fetchServices();
    }if(searchText.length === 0){
      setPageNumber(1);
      setHasMore(true);
      setServiceList([]);
      fetchServices();
    }
  },[searchText]);

  const handleSearchTextChange = (text) => {
    setSearchText(text);    
  };

  const handleClearSearch = () => {
    handleSearchTextChange("");
  };

  // const CustomFooter  = (totalRows) => {
  //   return (
  //     <GridFooterContainer>
  //       Total Rows : {totalRows}
  //       <GridFooter sx={{
  //         border: 'none', // To delete double border.
  //          padding: 10, 
  //          textAlign: "center"
  //         }} />
  //     </GridFooterContainer>
  //   );
  // }

  function CustomFooter(props) {
    const { totalCount , currentRecords} = props; 
    return (
      <Box style={{ paddingTop: 30, textAlign: 'center', fontSize: 14 }}>
        {loadingRecords ? (
        <CircularProgress size={24} color="inherit" />
      ) : (
        `Showing : ${currentRecords} - Total Records: ${totalCount}`
      )}
        
      </Box>
    );
  }
  // const { gridApiRef } = useTableScrollInfinite({
  //   isLoading: loading, // Replace with your actual loading state
  //   threshold: 10, // Adjust threshold as needed
  //   onNextPage: fetchServices, // Fetch next page
  // });
  // const fetchServices = async (keyword="" ) => {
  //   if (loading) return; // Prevent duplicate calls    
  //   //setLoading(true);
  //   const { page, pageSize } = servicePaginationModel;  
  //   try {
  //     const response = await GetService(0, pageSize, page + 1, keyword, navigate);          
  //     const fileredData = filterServices(response.result);
  //     setServiceList(prev => [...prev, ...fileredData]); // Append new data
  //     //setServicePaginationModel(prev => ({ ...prev, page: prev.page + 1 }));
  //   } catch (error) {
  //     console.error("Error fetching services:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  

  // const handlePaginationChange = (newModel) => {
  //   setServicePaginationModel(newModel);
  //   fetchServices();
  // };

  // ✅ Handle Scroll Event
  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 400) {
      fetchServices(); // Load next page when reaching bottom
    }
  }, [loading, hasMore]);
  
  // useEffect(() => {
  //   window.addEventListener("scroll", handleScroll);
  //   return () => window.removeEventListener("scroll", handleScroll);
  // }, [handleScroll]);  

const fetchBundles = async () => {
  try {
    setLoading(true);
    const data = await GetBundle(0, navigate);
    let fileredData = filterBundle(data.result);
    
    // Sort bundles by bundleName A→Z by default
    fileredData = fileredData.sort((a, b) => {
      // First by creation date (newest first) or by bundle name
      return a.bundleName?.localeCompare(b.bundleName);
    });
    
    setBundleList(fileredData);               
  } catch (error) {
    console.error("Error fetching bundle data:", error);
  } finally {
    setLoading(false);
  }
}

  useEffect(()=>{
    //fetchServices();
    if(hasBundleGetPermission){
      fetchBundles();
    }
  },[hasBundleGetPermission]);

  const MySwal = withReactContent(Swal);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenBundleDlg = () => {
    setBundleContent({title:'Create New Bundle', btnText:'Save Bundle', notification: 'Bundle saved successfully.'});
    setOpenBundleDlg(true);
  }
  const handleCloseBundleDlg = () => setOpenBundleDlg(false);
  const handleSaveBundle = () => {
    setBundleIsEmpty(false);
    handleCloseBundleDlg();
  };

  const handleOpenServiceDlg = () => {
    setServiceContent({title:'Create New Service', btnText:'Save Service', notification: 'Service saved successfully.'});
    setOpenServiceDlg(true);
  };
  const handleCloseServiceDlg = () => setOpenServiceDlg(false);

  const {ServiceProps} = useServiceLogic({setLoading,toast,navigate, handleCloseServiceDlg, serviceContent,fetchServices});
  const {BundleProps} = useBundleLogic({setLoading,toast,navigate, handleCloseBundleDlg, bundleContent,fetchServices,fetchBundles,serviceList});
  
  
  const {service, setService} = ServiceProps;
  const {bundle, setBundle} = BundleProps;

  // const handleEditService = (id) => {
    
  //   setServiceContent({title: 'Edit Service', btnText:'Save Changes', notification: 'Service updated successfully.'});
  //   const service = serviceList.filter(x=> x.id === id)[0];
  //   // console.log(id)
  //   // console.log(service)
  //   setService({
  //     serviceOfferingID:service.id,
  //     serviceOfferingName:service.serviceOfferingName,
  //     description:service.description,
  //     industryID:service.industryID,
  //     flatRate:service.flatRate,
  //     isActive:service.isActive
  //   });
  //   setOpenServiceDlg(true);
  // }

  const handleEditService = (id) => {
  setServiceContent({ title: 'Edit Service', btnText:'Save Changes', notification: 'Service updated successfully.' });
  const service = serviceList.find(x => x.id === id || x.serviceOfferingID === id);
  if (!service) {
    console.warn('Service not found for edit:', id);
    return;
  }
  setService({
    serviceOfferingID: service.id,
    serviceOfferingName: service.serviceOfferingName,
    description: service.description,
    industryID: service.industryID,
    flatRate: service.flatRate,
    isActive: service.isActive
  });
  setOpenServiceDlg(true);
}

  const handleEditBundle = (id) => {
    setBundleContent({title: 'Edit Bundle', btnText:'Save Changes', notification: 'Bundle updated successfully.'});
    const bundle = bundleList.filter(x=> x.id === id)[0];
    setBundle({
      bundleID:bundle.id,
      bundleName:bundle.bundleName,
      description:bundle.description,      
      isActive:bundle.isActive,
      services:bundle.services
    });
    setOpenBundleDlg(true);
  }
  
  // const handleCloseConfirmation = () => {
  //   setOpenDeleteConfirmation(false);
  //   setServiceToDelete(null);
  // }
  // const handleConfirmDelete = () => {

  //   // Call the delete handler here
  //   handleDeleteService(serviceToDelete);
  //   fetchServices();
  //   setOpenDeleteConfirmation(false);
  // };


  const showConfirmationServiceDelete = (id) => {
    console.log('id', id)
    MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
     width: '390px', // Explicit width control
  padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
        cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
    }
  }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action here
        handleDeleteService(id);        
      }
    });

  };

  const handleDeleteService = async (id) => {    
    try {
      setLoading(true);
      const result = await DeleteService(id, navigate);
        if (result && !result.isError) {
          fetchServices();                    
          toast.success("Service deleted successfully!", {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
               theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
      } else {
          toast.error(result.message || 'Service failed to saved.', {
              position: "top-right",
              autoClose: 4000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
               theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
      }
    } catch (error) {
      console.error('Error deleting Service:', error);
    } finally {
      setLoading(false);
    }
  }

  const showConfirmationBundleDelete = (id) => {
     MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
     width: '390px', // Explicit width control
  padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
         cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
    }
  }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action here
        handleDeleteBundle(id);        
      }
    });
  };

  const handleDeleteBundle = async (id) => {    
    try {
      setLoading(true);
      const result = await DeleteBundle(id, navigate);
        if (result && !result.isError) {
          fetchBundles();                    
          toast.success("Bundle deleted successfully!", {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
               theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
      } else {
          toast.error(result.message || 'Bundle failed to saved.', {
              position: "top-right",
              autoClose: 4000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
                theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
      }
    } catch (error) {
      console.error('Error deleting Bundle:', error);
    } finally {
      setLoading(false);
    }
  }

  //#endregion end of handlers and methods

  //#region IndexProps
  const IndexProps = {
    contentHeightFixed,
    loading,
    tabValue,
    handleTabChange,
    bundleIsEmpty,
    serviceIsEmpty,
    openBundleDlg,
    openServiceDlg,
    handleOpenBundleDlg,
    handleCloseBundleDlg,
    handleSaveBundle,
    openBundle,
    setOpenBundle,
    columnsBundle,
    columnsService,
    handleOpenServiceDlg,
    handleCloseServiceDlg,
    openService,
    setOpenService,
    //handleSaveService,
    navigate,
    setLoading,
    serviceList,
    bundleList,    
    bundlePaginationModel, 
    setBundlePaginationModel,
    servicePaginationModel,
    setServicePaginationModel,
    serviceContent,
    handleEditService,
    ServiceProps,
    bundleContent,
    BundleProps,
    pageSize,
    fetchServices,
    hasMore,    
    setPageSize,
    pageNumber,
    CustomFooter,
    searchText,
    handleSearchTextChange,
    handleClearSearch,
    handleDeleteService,
    handleDeleteBundle,
    handleEditBundle,
    showConfirmationServiceDelete
  };
  //#endregion end of IndexProps

  return {
    IndexProps,
  };
};
